import { measurements, type Measurement } from '../geom';
import {
  ancestorsOf,
  isDimension,
  isGroup,
  isLayered,
  isView,
  STYLE_KEYS,
  type DrawingDocument,
  type NodeId,
  type Style,
} from '../model';
import type { ResolvedScene } from './resolve';

export interface StyleProperty {
  readonly key: keyof Style;
  readonly value: Style[keyof Style];
  /** Where the value comes from: the object's own style, or its layer's. */
  readonly from: 'object' | 'layer';
}

/** Everything known about one drawn object, for the Properties card. */
export interface ObjectProperties {
  readonly id: NodeId;
  readonly name: string;
  /** `line`, `circle`, `arc`, `view`, or a dimension's kind, such as `linear dimension`. */
  readonly kind: string;
  readonly layer: string;
  /** Only the properties the object or its layer sets; the rest are the surface's default. */
  readonly style: readonly StyleProperty[];
  /** False when it, a group around it, or its layer is hidden. */
  readonly visible: boolean;
  /** True when it or its layer is locked, so it cannot be picked on the canvas. */
  readonly locked: boolean;
  /** The names of the views it is drawn in. */
  readonly views: readonly string[];
  /** The names of the groups around it, innermost first. */
  readonly groups: readonly string[];
  /** Why it could not be drawn, when it could not. */
  readonly error?: string;
  readonly measurements: readonly Measurement[];
}

export function describeObject(
  doc: DrawingDocument,
  scene: ResolvedScene,
  id: NodeId,
): ObjectProperties | null {
  const node = doc.nodes[id];
  if (!node || !isLayered(node)) return null;
  const layer = doc.layers.find((each) => each.id === node.layerId);
  const drawn =
    scene.entities.find((each) => each.id === id) ??
    scene.dimensions.find((each) => each.id === id) ??
    scene.views.find((each) => each.id === id);
  const entity = scene.entities.find((each) => each.id === id);
  const error = scene.errors[id];

  const style: StyleProperty[] = [];
  for (const key of STYLE_KEYS) {
    const own = node.style?.[key];
    const inherited = layer?.style?.[key];
    if (own !== undefined) style.push({ key, value: own, from: 'object' });
    else if (inherited !== undefined) style.push({ key, value: inherited, from: 'layer' });
  }

  return {
    id,
    name: node.name,
    kind: isDimension(node) ? `${node.kind} dimension` : node.type,
    layer: layer?.name ?? node.layerId,
    style,
    visible: drawn?.visible ?? false,
    locked: node.locked || (layer?.locked ?? false),
    views: Object.values(doc.nodes)
      .filter((each) => isView(each) && each.memberIds.includes(id))
      .map((each) => each.name),
    groups: ancestorsOf(doc, id).flatMap((groupId) => {
      const group = doc.nodes[groupId];
      return group && isGroup(group) ? [group.name] : [];
    }),
    ...(error === undefined ? {} : { error }),
    measurements: entity ? measurements(entity.shape) : [],
  };
}
